import {
  Anchor,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { RichTextEditor } from '@/frontend/components/marina/RichTextEditor'
import { useCreateInventoryItem } from '@/frontend/hooks/useInventory'

const UNITS = [
  { value: 'pcs', label: 'pcs' },
  { value: 'kg', label: 'kg' },
  { value: 'L', label: 'L' },
  { value: 'm', label: 'm' },
  { value: 'box', label: 'box' },
  { value: 'roll', label: 'roll' },
]

const CATEGORIES = [
  { value: 'Cleaning supplies', label: 'Cleaning supplies' },
  { value: 'Safety', label: 'Safety' },
  { value: 'Dock equipment', label: 'Dock equipment' },
  { value: 'Office', label: 'Office' },
  { value: 'Fuel', label: 'Fuel' },
  { value: 'Maintenance', label: 'Maintenance' },
  { value: 'Other', label: 'Other' },
]

export const Route = createFileRoute('/dashboard/facility/inventory/new')({
  component: NewInventoryItem,
})

function NewInventoryItem() {
  const navigate = useNavigate()
  const create = useCreateInventoryItem()

  const [name, setName] = useState('')
  const [category, setCategory] = useState<string | null>(null)
  const [initialStock, setInitialStock] = useState(0)
  const [lowThreshold, setLowThreshold] = useState(0)
  const [unit, setUnit] = useState('pcs')
  const [notes, setNotes] = useState('')

  async function submit() {
    try {
      const { item } = await create.mutateAsync({
        name: name.trim(),
        category,
        initialStock,
        lowThreshold,
        unit,
        notes: notes || null,
      })
      notifications.show({ title: 'Inventory item created', message: item.name, color: 'teal' })
      navigate({ to: '/dashboard/facility/inventory' })
    } catch (e: unknown) {
      notifications.show({
        title: 'Failed',
        message: e instanceof Error ? e.message : 'Try again',
        color: 'red',
      })
    }
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Breadcrumbs>
          <Anchor component={Link} to="/dashboard/facility/inventory" c="dimmed">
            Inventory
          </Anchor>
          <span>New Item</span>
        </Breadcrumbs>

        {/* Basic Information */}
        <Paper p="lg" radius="md">
          <Stack gap="md">
            <div>
              <Title order={4}>Basic Information</Title>
              <Text c="dimmed" size="sm">
                Essential details about the inventory item
              </Text>
            </div>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput
                label="Item Name"
                placeholder="Enter item name"
                required
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
              <Select
                label="Category"
                placeholder="Select category"
                data={CATEGORIES}
                value={category}
                onChange={setCategory}
                clearable
                searchable
              />
            </SimpleGrid>
          </Stack>
        </Paper>

        {/* Stock Levels */}
        <Paper p="lg" radius="md">
          <Stack gap="md">
            <div>
              <Title order={4}>Stock Levels</Title>
              <Text c="dimmed" size="sm">
                Current stock and threshold levels for inventory management
              </Text>
            </div>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <NumberInput
                label="Initial Stock"
                value={initialStock}
                onChange={(v) => setInitialStock(typeof v === 'number' ? v : 0)}
                min={0}
                step={1}
                decimalScale={3}
                placeholder="0.000"
              />
              <NumberInput
                label="Low Stock Threshold"
                value={lowThreshold}
                onChange={(v) => setLowThreshold(typeof v === 'number' ? v : 0)}
                min={0}
                step={1}
                decimalScale={1}
                placeholder="0,0"
              />
              <Select label="Unit of Measurement" data={UNITS} value={unit} onChange={(v) => setUnit(v ?? 'pcs')} />
            </SimpleGrid>
          </Stack>
        </Paper>

        {/* Additional Notes */}
        <Paper p="lg" radius="md">
          <Stack gap="md">
            <div>
              <Title order={4}>Additional Notes</Title>
              <Text c="dimmed" size="sm">
                Optional notes about this inventory item
              </Text>
            </div>
            <RichTextEditor value={notes} onChange={setNotes} placeholder="Add any relevant notes about this item…" />
          </Stack>
        </Paper>

        <Divider />

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => navigate({ to: '/dashboard/facility/inventory' })}>
            Cancel
          </Button>
          <Button color="laut" onClick={submit} loading={create.isPending} disabled={!name.trim()}>
            Create
          </Button>
        </Group>
      </Stack>
    </Container>
  )
}
